import Head from 'next/head'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const toAbsolute = (value: string): string =>
    /^https?:\/\//.test(value) ? value : `${siteConfig.assetsUrl ?? siteConfig.url}${value}`

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    type = 'website'
}): ReactNode => {
    const router = useRouter()

    // "Page | Czechmate Consulting" when it fits in 60 characters, otherwise the page title alone.
    const withName = title ? `${title} | ${siteConfig.name}` : siteConfig.name
    const pageTitle = title && withName.length > 60 ? title : withName

    const path = (router.asPath || '/').split(/[?#]/)[0]
    const pageUrl = `${siteConfig.url}${path === '/' ? '' : path}`
    const image = ogImage ? toAbsolute(ogImage) : undefined

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <link rel='canonical' href={pageUrl} />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content={type} />
            <meta property='og:url' content={pageUrl} />
            <meta property='og:locale' content='en_US' />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {image && <meta property='og:image' content={image} />}
            {image && <meta name='twitter:image' content={image} />}
        </Head>
    )
}
